/**
 * Commerce · Inventory.
 */
import { useMemo, useState } from "react";
import { Boxes, AlertOctagon } from "lucide-react";
import { Card, SectionHeader, TextInput, Button } from "../../components/lib";
import { TabBar, Drawer, Field, Segmented } from "../../components/lib.overlay";
import { AuditTrail } from "../../components/AuditTrail";
import { DocumentActions } from "../../components/DocumentActions";
import { useDeepLink } from "../../lib/integration";
import { STOCK, StockLevel, StockRow, PRODUCTS } from "../../mocks/commerceData";
import { BATCHES, MOVEMENTS, SUPPLIERS } from "../../mocks/operationsData";
import { PROCUREMENT } from "../../mocks/procurementData";

type SkuTab = "overview" | "batches" | "movements" | "procurement" | "audit";

const LEVEL_LABEL: Record<StockLevel, string> = {
  critical: "Critical", low: "Low", healthy: "Healthy", overstocked: "Overstocked",
};
const LEVEL_TINT: Record<StockLevel, { bg: string; fg: string }> = {
  critical:    { bg: "var(--ph-danger-tint)",  fg: "var(--ph-danger)"        },
  low:         { bg: "var(--ph-warning-tint)", fg: "var(--ph-warning)"       },
  healthy:     { bg: "var(--ph-success-tint)", fg: "var(--ph-success)"       },
  overstocked: { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-soft)"      },
};

export default function Inventory() {
  const { q: initialQ } = useDeepLink();
  const [tab, setTab] = useState<StockLevel | "all">("all");
  const [q, setQ] = useState(initialQ);
  const [selected, setSelected] = useState<StockRow | null>(null);
  const [skuTab, setSkuTab] = useState<SkuTab>("overview");

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: STOCK.length };
    STOCK.forEach((s) => { c[s.level] = (c[s.level] || 0) + 1; });
    return c;
  }, []);

  const filtered = useMemo(
    () => STOCK.filter((s) =>
      (tab === "all" || s.level === tab) &&
      (!q || s.name.toLowerCase().includes(q.toLowerCase()) || s.sku.toLowerCase().includes(q.toLowerCase())),
    ),
    [tab, q],
  );

  const totalUnits = STOCK.reduce((n, s) => n + s.onHand, 0);
  const incomingUnits = STOCK.reduce((n, s) => n + s.incoming, 0);
  const critCount = STOCK.filter((s) => s.level === "critical").length;

  return (
    <div className="space-y-6" data-testid="cm-inventory">
      <Card testId="cm-inv-summary">
        <SectionHeader eyebrow="Warehouse · Nasr City" title="Stock summary" action={<Boxes size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Units on hand</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>{totalUnits.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Incoming</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>{incomingUnits.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>SKUs tracked</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>{STOCK.length}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Critical SKUs</div>
            <div className="text-xl font-semibold tabular-nums mt-1 inline-flex items-center gap-1" style={{ color: critCount > 0 ? "var(--ph-danger)" : "var(--ph-ink-strong)" }}>
              {critCount > 0 && <AlertOctagon size={16} />} {critCount}
            </div>
          </div>
        </div>
      </Card>

      <Card testId="cm-inv-filters">
        <div className="flex items-center gap-3">
          <div className="flex-1"><TextInput value={q} onChange={setQ} placeholder="Search SKU or product…" testId="cm-inv-search" /></div>
          <Button testId="cm-inv-reorder">Order restock</Button>
        </div>
        <div className="mt-4">
          <TabBar<StockLevel | "all">
            testId="cm-inv-tabs"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: "all",         label: "All",         count: counts.all         },
              { key: "critical",    label: "Critical",    count: counts.critical    },
              { key: "low",         label: "Low",         count: counts.low         },
              { key: "healthy",     label: "Healthy",     count: counts.healthy     },
              { key: "overstocked", label: "Overstocked", count: counts.overstocked },
            ]}
          />
        </div>
      </Card>

      <Card padded={false} testId="cm-inv-table">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Levels" title={`${filtered.length} SKU${filtered.length === 1 ? "" : "s"}`} />
        </div>
        <div className="p-5">
          <div className="hidden md:grid text-[10px] uppercase tracking-[0.14em] pb-2" style={{ color: "var(--ph-ink-muted)", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr" }}>
            <div>Product</div>
            <div className="text-right">On hand</div>
            <div className="text-right">Reserved</div>
            <div className="text-right">Incoming</div>
            <div className="text-right">Days cover</div>
            <div className="text-right">Level</div>
          </div>
          <div className="space-y-1">
            {filtered.map((s) => {
              const tint = LEVEL_TINT[s.level];
              const pct = Math.min(100, Math.max(2, (s.onHand / Math.max(s.reorderPoint * 2, 1)) * 100));
              return (
                <div key={s.sku} data-testid={`cm-inv-${s.sku}`}
                  onClick={() => { setSelected(s); setSkuTab("overview"); }}
                  role="button" tabIndex={0}
                  className="grid items-center py-2 cursor-pointer transition-colors hover:bg-[var(--ph-surface-hover)]"
                  style={{ gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr", borderTop: "1px solid var(--ph-border-subtle)" }}>
                  <div className="min-w-0">
                    <div className="text-[10px] font-mono truncate" style={{ color: "var(--ph-ink-muted)" }}>{s.sku}</div>
                    <div className="text-sm font-semibold truncate" style={{ color: "var(--ph-ink-strong)" }}>{s.name}</div>
                    <div className="mt-1 h-1 rounded" style={{ background: "var(--ph-border-subtle)", overflow: "hidden", width: 200 }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: tint.fg }} />
                    </div>
                  </div>
                  <div className="text-sm tabular-nums text-right" style={{ color: "var(--ph-ink-strong)" }}>{s.onHand}</div>
                  <div className="text-sm tabular-nums text-right" style={{ color: "var(--ph-ink-default)" }}>{s.reserved}</div>
                  <div className="text-sm tabular-nums text-right" style={{ color: "var(--ph-ink-default)" }}>{s.incoming || "—"}</div>
                  <div className="text-sm tabular-nums text-right" style={{ color: s.level === "critical" ? "var(--ph-danger)" : "var(--ph-ink-default)" }}>{s.daysCover}d</div>
                  <div className="flex justify-end">
                    <span className="px-2 py-0.5 text-[10px] font-semibold uppercase"
                      style={{ letterSpacing: "0.14em", background: tint.bg, color: tint.fg, borderRadius: "var(--ph-radius-sm)" }}>
                      {LEVEL_LABEL[s.level]}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      <Drawer open={Boolean(selected)} onClose={() => setSelected(null)} title="SKU 360" testId="cm-sku-drawer" width={620}>
        {selected && (() => {
          const product = PRODUCTS.find((p) => p.sku === selected.sku);
          const batches = BATCHES.filter((b) => b.sku === selected.sku);
          const movements = MOVEMENTS.filter((m) => m.sku === selected.sku);
          const relatedPOs = PROCUREMENT.filter((po) => po.linkedTo === selected.sku || (po.linkedTo || "").includes(selected.sku));
          const supplierNames = new Set(relatedPOs.map((po) => po.supplier).filter(Boolean));
          const relatedSuppliers = SUPPLIERS.filter((s) => supplierNames.has(s.name));
          const avgCost = batches.length ? batches.reduce((n, b: any) => n + (b.costEGPPerUnit || b.costEGP || 0), 0) / batches.length : 0;
          const invValue = selected.onHand * (avgCost || (product ? product.priceEGP * 0.4 : 0));
          const available = selected.onHand - selected.reserved;
          const outgoing30d = movements.filter((m) => m.kind === "consumption" || m.kind === "transfer").reduce((n, m) => n + Math.abs(m.qty), 0);
          const tint = LEVEL_TINT[selected.level];
          return (
            <div>
              <div className="text-[10px] font-mono" style={{ color: "var(--ph-ink-muted)" }}>{selected.sku}</div>
              <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.35rem", color: "var(--ph-ink-strong)" }} className="mt-1 mb-2">{selected.name}</div>
              <div className="flex items-center gap-2 mb-4">
                <span className="px-2 py-0.5 text-[10px] font-semibold uppercase"
                  style={{ letterSpacing: "0.14em", background: tint.bg, color: tint.fg, borderRadius: "var(--ph-radius-sm)" }}>
                  {LEVEL_LABEL[selected.level]}
                </span>
                <span className="text-xs" style={{ color: "var(--ph-ink-muted)" }}>{selected.location}</span>
              </div>

              <div className="mb-4 pb-3" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
                <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>SKU Inventory Report · PDF · Excel · CSV · Print</div>
                <DocumentActions docKey="sku-inventory" recordId={selected.sku} testId={`cm-sku-report-${selected.sku}`} compact />
              </div>

              <div className="mb-4 overflow-x-auto">
                <Segmented<SkuTab>
                  testId="cm-sku-ws-tabs"
                  value={skuTab}
                  onChange={setSkuTab}
                  options={[
                    { value: "overview",    label: "Overview"    },
                    { value: "batches",     label: "Batches"     },
                    { value: "movements",   label: "Movements"   },
                    { value: "procurement", label: "Procurement" },
                    { value: "audit",       label: "Audit"       },
                  ]}
                />
              </div>

              {skuTab === "overview" && (
                <div data-testid="cm-sku-ws-overview">
                  <Field label="On hand"        inline><span className="text-sm tabular-nums">{selected.onHand}</span></Field>
                  <Field label="Reserved"       inline><span className="text-sm tabular-nums">{selected.reserved}</span></Field>
                  <Field label="Available"      inline><span className="text-sm tabular-nums" style={{ color: available > 0 ? "var(--ph-ink-strong)" : "var(--ph-danger)" }}>{available}</span></Field>
                  <Field label="Incoming"       inline><span className="text-sm tabular-nums">{selected.incoming || "—"}</span></Field>
                  <Field label="Outgoing (30d)" inline><span className="text-sm tabular-nums">{outgoing30d}</span></Field>
                  <Field label="Days cover"     inline><span className="text-sm tabular-nums" style={{ color: selected.level === "critical" ? "var(--ph-danger)" : "var(--ph-ink-strong)" }}>{selected.daysCover}d</span></Field>
                  <Field label="Reorder point"  inline><span className="text-sm tabular-nums">{selected.reorderPoint}</span></Field>
                  <Field label="Average cost"   inline><span className="text-sm tabular-nums">EGP {avgCost.toFixed(0)}</span></Field>
                  <Field label="Inventory value"inline><span className="text-sm tabular-nums">EGP {invValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></Field>
                  <Field label="Warehouse"      inline><span className="text-sm">{selected.location}</span></Field>
                </div>
              )}

              {skuTab === "batches" && (
                <div data-testid="cm-sku-ws-batches">
                  <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Batches · {batches.length}</div>
                  {batches.length === 0 && <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No batches for this SKU.</div>}
                  {batches.map((b) => (
                    <div key={b.id} data-testid={`cm-sku-batch-${b.id}`} className="py-2" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
                      <div className="text-sm font-medium" style={{ color: "var(--ph-ink-strong)" }}>{b.code}</div>
                      <div className="text-xs" style={{ color: "var(--ph-ink-muted)" }}>{b.stage} · {b.units} units · yield {b.yieldPct}% · aged {b.startedRelative}</div>
                    </div>
                  ))}
                </div>
              )}

              {skuTab === "movements" && (
                <div data-testid="cm-sku-ws-movements">
                  <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Recent movements · {movements.length}</div>
                  {movements.length === 0 && <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No movements recorded.</div>}
                  {movements.slice(0, 20).map((m) => (
                    <div key={m.id} data-testid={`cm-sku-mv-${m.id}`} className="py-2" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
                      <div className="text-sm font-medium" style={{ color: "var(--ph-ink-strong)" }}>{m.kind} · {m.qty > 0 ? "+" : ""}{m.qty}</div>
                      <div className="text-xs" style={{ color: "var(--ph-ink-muted)" }}>{m.from} → {m.to} · {m.reference} · {m.timestamp}</div>
                    </div>
                  ))}
                </div>
              )}

              {skuTab === "procurement" && (
                <div data-testid="cm-sku-ws-procurement">
                  <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Related POs · {relatedPOs.length}</div>
                  {relatedPOs.length === 0 && <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No POs reference this SKU.</div>}
                  {relatedPOs.map((po) => (
                    <div key={po.id} data-testid={`cm-sku-po-${po.id}`} className="py-2" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
                      <div className="text-sm font-medium" style={{ color: "var(--ph-ink-strong)" }}>{po.poNumber || po.id}</div>
                      <div className="text-xs" style={{ color: "var(--ph-ink-muted)" }}>{po.supplier} · {po.status} · EGP {(po.amountEGP || 0).toLocaleString()}</div>
                    </div>
                  ))}
                  <div className="text-[10px] uppercase tracking-[0.14em] mb-1 mt-3" style={{ color: "var(--ph-ink-muted)" }}>Suppliers · {relatedSuppliers.length}</div>
                  {relatedSuppliers.length === 0 && <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No suppliers linked.</div>}
                  {relatedSuppliers.map((s) => (
                    <div key={s.id} data-testid={`cm-sku-sup-${s.id}`} className="py-2" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
                      <div className="text-sm font-medium" style={{ color: "var(--ph-ink-strong)" }}>{s.name}</div>
                      <div className="text-xs" style={{ color: "var(--ph-ink-muted)" }}>Risk: {s.risk} · OTIF {s.onTimeRate}% · {s.city}</div>
                    </div>
                  ))}
                </div>
              )}

              {skuTab === "audit" && (
                <div data-testid="cm-sku-ws-audit">
                  <AuditTrail collection="stock" recordId={selected.sku} testId="cm-sku-audit" />
                </div>
              )}
            </div>
          );
        })()}
      </Drawer>
    </div>
  );
}
